import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  getAllUsers,
  getAllSubscriptions,
  getAllSubscriptionTypes
} from "../../services/AdminService";
import {
  UsersIcon,
  CreditCardIcon,
  TagIcon,
  TrendingUpIcon,
  TrendingDownIcon,
  ClockIcon,
  CashIcon,
  ExclamationIcon,
  ArrowRightIcon,
  DocumentReportIcon,
} from "@heroicons/react/outline";

const DashboardCard = ({ title, value, icon: Icon, trend, trendValue, color = "blue", linkTo, description }) => {
  const colors = { bg: "bg-[#eb5527]", light: "bg-[#eb5527]/10", border: "border-[#eb5527]/30", text: "text-[#eb5527]" };

  const content = (
    <div className={`relative bg-white rounded-xl shadow-sm border ${colors.border} p-6 hover:shadow-md transition-all duration-300 group overflow-hidden h-full flex flex-col`}>
      <div className={`absolute inset-0 ${colors.light} opacity-30`}></div>
      <div className="relative z-10 flex flex-col h-full">
        {/* Header with icon */}
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center space-x-2">
            <p className="text-sm font-semibold text-gray-700 uppercase tracking-wide">{title}</p>
          </div>
          <div className={`${colors.bg} rounded-full p-3 flex-shrink-0`}>
            <Icon className="w-6 h-6 text-white" />
          </div>
        </div>

        {/* Value */}
        <div className="mb-3">
          <p className="text-3xl font-bold text-gray-900">{value}</p>
        </div>

        {/* Description - takes remaining space */}
        <div className="flex-1 flex flex-col justify-between">
          {description && (
            <p className="text-xs text-gray-500 leading-relaxed">{description}</p>
          )}

          {/* Trend indicator at bottom */}
          {trend && (
            <div className="flex items-center mt-3">
              {trend === 'up' ? (
                <TrendingUpIcon className="w-4 h-4 text-green-500 mr-1" />
              ) : (
                <TrendingDownIcon className="w-4 h-4 text-red-500 mr-1" />
              )}
              <span className={`text-sm font-medium ${trend === 'up' ? 'text-green-600' : 'text-red-600'}`}>
                {trendValue}
              </span>
            </div>
          )}
        </div>

        {/* Arrow indicator for links */}
        {linkTo && (
          <div className="flex justify-end mt-2">
            <ArrowRightIcon className="w-5 h-5 text-gray-400 group-hover:text-gray-600 transition-colors duration-200" />
          </div>
        )}
      </div>
    </div>
  );

  return linkTo ? (
    <Link to={linkTo} className="block transform hover:-translate-y-1 transition-transform duration-200">
      {content}
    </Link>
  ) : content;
};

const QuickStatsCard = ({ title, stats, linkTo, icon: Icon }) => {
  const content = (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-all duration-300 group">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          {Icon && (
            <div className="bg-gray-100 rounded-lg p-2">
              <Icon className="w-5 h-5 text-gray-600" />
            </div>
          )}
          <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
        </div>
        {linkTo && (
          <ArrowRightIcon className="w-5 h-5 text-gray-400 group-hover:text-gray-600 transition-colors duration-200" />
        )}
      </div>
      <div className="space-y-4">
        {stats.map((stat, index) => (
          <div key={index} className="flex justify-between items-center py-2 border-b border-gray-50 last:border-b-0">
            <span className="text-sm font-medium text-gray-600">{stat.label}</span>
            <span className={`text-sm font-bold ${stat.color || 'text-gray-900'} px-2 py-1 rounded-full ${stat.color?.includes('green') ? 'bg-green-50' : stat.color?.includes('red') ? 'bg-red-50' : stat.color?.includes('yellow') ? 'bg-yellow-50' : stat.color?.includes('blue') ? 'bg-blue-50' : stat.color?.includes('customOrange') ? 'bg-[#eb5527]/10' : 'bg-gray-50'}`}>
              {stat.value}
            </span>
          </div>
        ))}
      </div>
    </div>
  );

  return linkTo ? (
    <Link to={linkTo} className="block transform hover:-translate-y-1 transition-transform duration-200">
      {content}
    </Link>
  ) : content;
};

const RecentActivityCard = ({ activities }) => (
  <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
    <div className="flex items-center space-x-3 mb-6">
      <div className="bg-blue-100 rounded-lg p-2">
        <DocumentReportIcon className="w-5 h-5 text-blue-600" />
      </div>
      <h3 className="text-lg font-semibold text-gray-900">System Overview</h3>
    </div>
    <div className="space-y-4">
      {activities.length > 0 ? (
        activities.slice(0, 5).map((activity, index) => (
          <div key={index} className="flex items-start space-x-4 p-3 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors duration-200">
            <div className={`w-3 h-3 rounded-full ${activity.color} mt-1 flex-shrink-0`}></div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900 leading-5">{activity.description}</p>
              <p className="text-xs text-gray-500 mt-1">{activity.time}</p>
            </div>
          </div>
        ))
      ) : (
        <div className="text-center py-8">
          <DocumentReportIcon className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-sm text-gray-500">No recent activities</p>
        </div>
      )}
    </div>
  </div>
);

const DashboardOverviewPage = () => {
  const [users, setUsers] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [subscriptionTypes, setSubscriptionTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Helper function to check and update expired subscriptions (same as in SubscriptionManagementPage)
  const checkAndUpdateExpiredStatus = (subscriptionsList) => {
    const now = new Date();
    return subscriptionsList.map(subscription => {
      if (subscription.end_date && subscription.status === 'active') {
        const endDate = new Date(subscription.end_date);
        if (endDate <= now) {
          return { ...subscription, status: 'expired' };
        }
      }
      return subscription;
    });
  };

  // Helper function to calculate remaining time
  const calculateRemainingTime = (endDate) => {
    if (!endDate) return null;

    const now = new Date();
    const end = new Date(endDate);
    const diffMs = end - now;

    if (diffMs <= 0) return { expired: true };

    const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    return { days, expired: false };
  };

  // Helper function to safely format currency
  const formatCurrency = (value) => {
    const num = parseFloat(value);
    return isNaN(num) ? '0.00' : num.toFixed(2);
  };

  useEffect(() => {
    fetchDashboardData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const [usersRes, subscriptionsRes, typesRes] = await Promise.all([
        getAllUsers(),
        getAllSubscriptions(),
        getAllSubscriptionTypes()
      ]);

      if (usersRes?.Data) {
        setUsers(usersRes.Data);
      }

      if (subscriptionsRes?.Data?.subscriptions) {
        const updatedSubscriptions = checkAndUpdateExpiredStatus(subscriptionsRes.Data.subscriptions);
        setSubscriptions(updatedSubscriptions);
      }

      if (typesRes?.Data) {
        setSubscriptionTypes(typesRes.Data);
      }
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
      setError(error.message || "An error occurred while fetching dashboard data");
    } finally {
      setLoading(false);
    }
  };

  // Calculate metrics
  const totalUsers = users.length;
  const activeUsers = users.filter(user => user.isActive).length;
  const totalSubscriptions = subscriptions.length;
  const activeSubscriptions = subscriptions.filter(sub => sub.status === 'active').length;
  const expiredSubscriptions = subscriptions.filter(sub => sub.status === 'expired').length;
  const cancelledSubscriptions = subscriptions.filter(sub => sub.status === 'cancelled').length;
  const totalSubscriptionTypes = subscriptionTypes.length;
  const activeSubscriptionTypes = subscriptionTypes.filter(type => type.isActive).length;

  // Calculate projected monthly revenue based on current active subscriptions
  const calculateProjectedRevenue = () => {
    return subscriptions
      .filter(sub => sub.status === 'active')
      .reduce((sum, sub) => {
        const type = subscriptionTypes.find(t => t._id === sub.subscription_type_id);
        const price = parseFloat(type?.price || 0);
        if (isNaN(price)) return sum;

        // Convert to monthly projection based on billing frequency
        let monthlyPrice = 0;
        switch (type?.billing_frequency) {
          case 'monthly':
            monthlyPrice = price;
            break;
          case 'quarterly':
            monthlyPrice = price / 3;
            break;
          case 'annual':
            monthlyPrice = price / 12;
            break;
          case 'once':
            monthlyPrice = 0; // One-time payments don't contribute to monthly projections
            break;
          default:
            monthlyPrice = 0;
        }

        return sum + monthlyPrice;
      }, 0);
  };

  const projectedRevenue = calculateProjectedRevenue();

  // User role distribution
  const adminUsers = users.filter(user => user.role_name === 'admin').length;
  const subscriberUsers = users.filter(user => user.role_name === 'subscriber').length;
  const freeUsers = users.filter(user => user.role_name === 'free user').length;

  // Subscriptions expiring soon (within 7 days)
  const expiringSoon = subscriptions.filter(sub => {
    if (sub.status !== 'active' || !sub.end_date) return false;
    const remaining = calculateRemainingTime(sub.end_date);
    return !remaining.expired && remaining.days <= 7;
  }).length;

  // Recent activities (mock data based on our system)
  const recentActivities = [
    {
      description: `${totalUsers} total users in system`,
      time: 'Current status',
      color: 'bg-blue-400'
    },
    {
      description: `${activeSubscriptions} active subscriptions`,
      time: 'Current status',
      color: 'bg-green-400'
    },
    {
      description: `${expiredSubscriptions} expired subscriptions`,
      time: 'Current status',
      color: 'bg-red-400'
    },
    {
      description: `${expiringSoon} subscriptions expiring within 7 days`,
      time: 'Attention needed',
      color: 'bg-yellow-400'
    },
    {
      description: `$${formatCurrency(projectedRevenue)} projected monthly revenue`,
      time: 'Current calculation',
      color: 'bg-purple-400'
    }
  ];

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mb-8">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center">
            <ExclamationIcon className="w-5 h-5 text-red-400 mr-2" />
            <p className="text-red-700">Error loading dashboard: {error}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mb-8">
      {/* Quick Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-8 items-stretch">
        <DashboardCard
          title="Total Users"
          value={totalUsers}
          icon={UsersIcon}
          linkTo="/dashboard/user-management"
          description="Total number of registered users in the system"
        />
        <DashboardCard
          title="Active Subscriptions"
          value={activeSubscriptions}
          icon={CreditCardIcon}
          linkTo="/dashboard/subscription-management"
          description="Number of currently active subscription plans"
        />
        <DashboardCard
          title="Types of Subscription"
          value={totalSubscriptionTypes}
          icon={TagIcon}
          linkTo="/dashboard/subscription-types"
          description="Available subscription plan types"
        />
        <DashboardCard
          title="Projected Revenue/Month"
          value={`$${formatCurrency(projectedRevenue)}`}
          icon={CashIcon}
          description="Estimated monthly revenue from active subscriptions"
        />
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-all duration-300 relative h-full flex flex-col">
          <div className="absolute inset-0 bg-gray-50 opacity-30 rounded-xl"></div>
          <div className="relative z-10 flex flex-col h-full">
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center space-x-2">
                <p className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Total Revenue</p>
              </div>
              <div className="bg-gray-400 rounded-full p-3 flex-shrink-0">
                <CashIcon className="w-6 h-6 text-white" />
              </div>
            </div>
            <div className="mb-3">
              <p className="text-3xl font-bold text-gray-400">$---</p>
            </div>
            <div className="flex-1 flex flex-col justify-between">
              <p className="text-xs text-gray-500 leading-relaxed">Historical revenue tracking and analytics</p>
              <div className="flex items-center mt-3">
                <span className="text-xs text-yellow-600 bg-yellow-100 px-2 py-1 rounded-full">
                  In Development
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Detailed Stats */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        <QuickStatsCard
          title="User Statistics"
          linkTo="/dashboard/user-management"
          stats={[
            { label: 'Total Users', value: totalUsers },
            { label: 'Active Users', value: activeUsers, color: 'text-green-600' },
            { label: 'Admin Users', value: adminUsers, color: 'text-blue-600' },
            { label: 'Subscriber Users', value: subscriberUsers, color: 'text-[#eb5527]' },
            { label: 'Free Users', value: freeUsers, color: 'text-gray-600' }
          ]}
        />

        <QuickStatsCard
          title="Subscription Overview"
          linkTo="/dashboard/subscription-management"
          stats={[
            { label: 'Total Subscriptions', value: totalSubscriptions },
            { label: 'Active', value: activeSubscriptions, color: 'text-green-600' },
            { label: 'Expired', value: expiredSubscriptions, color: 'text-red-600' },
            { label: 'Cancelled', value: cancelledSubscriptions, color: 'text-gray-600' },
            { label: 'Expiring Soon (7d)', value: expiringSoon, color: 'text-yellow-600' }
          ]}
        />

        <QuickStatsCard
          title="Subscription Types"
          linkTo="/dashboard/subscription-types"
          stats={[
            { label: 'Total Types', value: totalSubscriptionTypes },
            { label: 'Active Types', value: activeSubscriptionTypes, color: 'text-green-600' },
            { label: 'Inactive Types', value: totalSubscriptionTypes - activeSubscriptionTypes, color: 'text-gray-600' },
            { label: 'Avg. Price', value: `$${subscriptionTypes.length > 0 ? formatCurrency(subscriptionTypes.reduce((sum, type) => sum + (parseFloat(type.price) || 0), 0) / subscriptionTypes.length) : '0.00'}` },
            { label: 'Projected/Month', value: `$${formatCurrency(projectedRevenue)}`, color: 'text-[#eb5527]' }
          ]}
        />
      </div>

      {/* Alerts Section */}
      {expiringSoon > 0 && (
        <div className="mb-8">
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex items-center">
              <ClockIcon className="w-5 h-5 text-yellow-400 mr-2" />
              <div>
                <h3 className="text-yellow-800 font-medium">Attention Required</h3>
                <p className="text-yellow-700 text-sm">
                  {expiringSoon} subscription{expiringSoon !== 1 ? 's' : ''} expiring within 7 days.{' '}
                  <Link to="/dashboard/subscription-management" className="underline hover:no-underline">
                    Review subscriptions →
                  </Link>
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Recent Activities and Quick Actions */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <RecentActivityCard activities={recentActivities} />

        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h3>
          <div className="space-y-3">
            <Link
              to="/dashboard/user-management"
              className="flex items-center w-full px-4 py-3 text-sm font-medium text-white bg-[#eb5527] rounded-md hover:bg-[#c9451d] transition-colors duration-200 group"
            >
              <UsersIcon className="w-5 h-5 mr-3" />
              <span>Manage Users</span>
              <svg className="w-4 h-4 ml-auto opacity-0 group-hover:opacity-100 transition-opacity" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </Link>
            <Link
              to="/dashboard/subscription-management"
              className="flex items-center w-full px-4 py-3 text-sm font-medium text-white bg-[#eb5527] rounded-md hover:bg-[#c9451d] transition-colors duration-200 group"
            >
              <CreditCardIcon className="w-5 h-5 mr-3" />
              <span>Manage Subscriptions</span>
              <svg className="w-4 h-4 ml-auto opacity-0 group-hover:opacity-100 transition-opacity" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </Link>
            <Link
              to="/dashboard/subscription-types"
              className="flex items-center w-full px-4 py-3 text-sm font-medium text-white bg-[#eb5527] rounded-md hover:bg-[#c9451d] transition-colors duration-200 group"
            >
              <TagIcon className="w-5 h-5 mr-3" />
              <span>Manage Subscription Types</span>
              <svg className="w-4 h-4 ml-auto opacity-0 group-hover:opacity-100 transition-opacity" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </Link>
            <button
              onClick={fetchDashboardData}
              className="flex items-center w-full px-4 py-3 text-sm font-medium text-[#eb5527] bg-[#eb5527]/10 rounded-md hover:bg-[#eb5527]/20 transition-colors duration-200 group border border-[#eb5527]/30"
            >
              <svg className="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              <span>Refresh Data</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardOverviewPage;
